import { Alert, Box } from "@mui/material";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { SaveResponse, SaveRoleRequest } from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import RoleForm, { RoleFormValues } from "./RoleForm";

const UpdateRole = () => {
  const { id } = useParams<{ id: string }>();
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const { roleService, roleDetailsUrl, rolesUrl } = useGlobalContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { isLoading, isError, data } = useQuery(
    ["role", id],
    () => roleService.getRole(id!),
    {
      enabled: !!id,
    }
  );

  const role = data?.data;
  useTitle(role?.name || t({ id: "updateRole" }));

  if (!id) return <Navigate to={rolesUrl()} replace />;
  if (isLoading) return <Loader />;
  if (isError || !data?.data) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="fetchError" />
        </Alert>
      </Box>
    );
  }

  const onSubmit = async (values: RoleFormValues) => {
    try {
      const updatedRole: SaveResponse = await registerPromise(
        roleService.saveRole(SaveRoleRequest.fromJS({ ...values, id }))
      );

      queryClient.invalidateQueries("roles");
      queryClient.invalidateQueries(["role", id]);

      navigate(roleDetailsUrl(id), { replace: true });

      return updatedRole;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  return (
    <>
      <Box my={2}>
        <RedirectBackButton
          redirectRoute={roleDetailsUrl(id)}
          labelKey="backToDetailsButtonLabel"
        />
      </Box>
      <RoleForm
        onSubmit={onSubmit}
        role={role}
        submitLabel={t({ id: "updateRole" })}
        onCancel={() => navigate(roleDetailsUrl(id))}
      />
    </>
  );
};

export default UpdateRole;
